/* The built-in UI string catalog. These are the app's own interface strings —
   labels the operator does not author (operator copy lives in the content
   layer and is edited per site in /admin as before). English is the complete
   reference catalog; every other locale falls back to it per key, and the
   operator can override any key per locale from Admin → Languages. */

/* A locale is a language, or a regional variation of one. A variation names
   its `base` and starts as a copy of it: its catalog column holds only what
   genuinely differs, and everything else falls back base-first, then to
   English. `settingKey` is the boolean that switches it on in the admin —
   locale codes carry a dash, which an object key cannot. */
export const LOCALES = [
  { code: "en", name: "English", nativeName: "English", settingKey: "en" },
  { code: "en-US", name: "English (US)", nativeName: "English (US)", base: "en", settingKey: "enUS" },
  { code: "sv", name: "Swedish", nativeName: "Svenska", settingKey: "sv" },
  { code: "de", name: "German", nativeName: "Deutsch", settingKey: "de" },
  { code: "es", name: "Spanish", nativeName: "Español", settingKey: "es" },
  { code: "es-MX", name: "Spanish (MX)", nativeName: "Español (MX)", base: "es", settingKey: "esMX" },
  { code: "pt", name: "Portuguese", nativeName: "Português", settingKey: "pt" },
  { code: "pt-BR", name: "Portuguese (BR)", nativeName: "Português (BR)", base: "pt", settingKey: "ptBR" },
] as const;

export type LocaleCode = (typeof LOCALES)[number]["code"];

export type LocaleSettingKey = Exclude<(typeof LOCALES)[number]["settingKey"], "en">;

export const DEFAULT_LOCALE: LocaleCode = "en";

export const baseOf = (code: LocaleCode): LocaleCode | null => {
  const locale = LOCALES.find((l) => l.code === code);
  return locale && "base" in locale ? locale.base : null;
};

const en = {
  "common.language": "Language",
  "common.close": "Close",
  "common.loading": "Loading…",
  "common.viewAll": "View all",
  "common.search": "Search",
  "topbar.expandNav": "Expand navigation",
  "topbar.searchGames": "Search games",
  "topbar.accountMenu": "Account menu",
  "topbar.viewAccount": "View account",
  "topbar.myAccount": "My Account",
  "topbar.bets": "Bets",
  "topbar.transactions": "Transactions",
  "topbar.rewards": "Rewards",
  "topbar.notifications": "Notifications",
  "topbar.wallet": "Wallet",
  "topbar.chat": "Chat",
  "topbar.signIn": "Sign in",
  "topbar.register": "Register",
  "common.email": "Email address",
  "topbar.signOut": "Sign out",
  "lobby.searchAll": "Search all games",
  "chat.signInToChat": "Log in to chat",
} satisfies Record<string, string>;

export type MessageKey = keyof typeof en;

export const MESSAGE_KEYS = Object.keys(en) as MessageKey[];

export const isBuiltInLocale = (value: string): value is LocaleCode => LOCALES.some((l) => l.code === value);

/* A message in `locale`: its own column first, then the base language's,
   then English. Variant columns ship empty on purpose — en-US, es-MX and
   pt-BR inherit their base until a string genuinely differs (here or as an
   operator override in the admin). An operator-added locale ("fr-CA") has
   no column of its own; its language part is tried against the built-in
   catalogs, and English covers the rest. */
export const resolveMessage = (locale: string, key: MessageKey): string => {
  const own = isBuiltInLocale(locale) && locale !== "en" ? CATALOG[locale]?.[key] : undefined;
  if (own !== undefined) return own;
  const base = isBuiltInLocale(locale) ? baseOf(locale) : locale.split("-")[0];
  const inherited = base && base !== "en" && isBuiltInLocale(base) ? CATALOG[base]?.[key] : undefined;
  return inherited ?? CATALOG.en[key];
};

export const CATALOG: { en: typeof en } & Partial<Record<LocaleCode, Partial<Record<MessageKey, string>>>> = {
  en,
  "en-US": {},
  "es-MX": {},
  "pt-BR": {
    "common.search": "Buscar",
    "topbar.searchGames": "Buscar jogos",
    "topbar.register": "Cadastrar",
    "lobby.searchAll": "Buscar todos os jogos",
    "chat.signInToChat": "Entre para bater papo",
  },
  sv: {
    "common.language": "Språk",
    "common.close": "Stäng",
    "common.loading": "Laddar…",
    "common.viewAll": "Visa alla",
    "common.search": "Sök",
    "topbar.expandNav": "Expandera navigering",
    "topbar.searchGames": "Sök spel",
    "topbar.accountMenu": "Kontomeny",
    "topbar.viewAccount": "Visa konto",
    "topbar.myAccount": "Mitt konto",
    "topbar.bets": "Spel",
    "topbar.transactions": "Transaktioner",
    "topbar.rewards": "Belöningar",
    "topbar.notifications": "Notiser",
    "topbar.wallet": "Plånbok",
    "topbar.chat": "Chatt",
    "topbar.signIn": "Logga in",
    "topbar.register": "Registrera dig",
    "common.email": "E-postadress",
    "topbar.signOut": "Logga ut",
    "lobby.searchAll": "Sök bland alla spel",
    "chat.signInToChat": "Logga in för att chatta",
  },
  de: {
    "common.language": "Sprache",
    "common.close": "Schließen",
    "common.loading": "Lädt…",
    "common.viewAll": "Alle anzeigen",
    "common.search": "Suchen",
    "topbar.expandNav": "Navigation ausklappen",
    "topbar.searchGames": "Spiele suchen",
    "topbar.accountMenu": "Kontomenü",
    "topbar.viewAccount": "Konto anzeigen",
    "topbar.myAccount": "Mein Konto",
    "topbar.bets": "Wetten",
    "topbar.transactions": "Transaktionen",
    "topbar.rewards": "Prämien",
    "topbar.notifications": "Benachrichtigungen",
    "topbar.wallet": "Wallet",
    "topbar.chat": "Chat",
    "topbar.signIn": "Anmelden",
    "topbar.register": "Registrieren",
    "common.email": "E-Mail-Adresse",
    "topbar.signOut": "Abmelden",
    "lobby.searchAll": "Alle Spiele durchsuchen",
    "chat.signInToChat": "Zum Chatten anmelden",
  },
  es: {
    "common.language": "Idioma",
    "common.close": "Cerrar",
    "common.loading": "Cargando…",
    "common.viewAll": "Ver todo",
    "common.search": "Buscar",
    "topbar.expandNav": "Expandir navegación",
    "topbar.searchGames": "Buscar juegos",
    "topbar.accountMenu": "Menú de cuenta",
    "topbar.viewAccount": "Ver cuenta",
    "topbar.myAccount": "Mi cuenta",
    "topbar.bets": "Apuestas",
    "topbar.transactions": "Transacciones",
    "topbar.rewards": "Recompensas",
    "topbar.notifications": "Notificaciones",
    "topbar.wallet": "Cartera",
    "topbar.chat": "Chat",
    "topbar.signIn": "Iniciar sesión",
    "topbar.register": "Registrarse",
    "common.email": "Correo electrónico",
    "topbar.signOut": "Cerrar sesión",
    "lobby.searchAll": "Buscar en todos los juegos",
    "chat.signInToChat": "Inicia sesión para chatear",
  },
  pt: {
    "common.language": "Idioma",
    "common.close": "Fechar",
    "common.loading": "Carregando…",
    "common.viewAll": "Ver tudo",
    "common.search": "Pesquisar",
    "topbar.expandNav": "Expandir navegação",
    "topbar.searchGames": "Pesquisar jogos",
    "topbar.accountMenu": "Menu da conta",
    "topbar.viewAccount": "Ver conta",
    "topbar.myAccount": "Minha conta",
    "topbar.bets": "Apostas",
    "topbar.transactions": "Transações",
    "topbar.rewards": "Recompensas",
    "topbar.notifications": "Notificações",
    "topbar.wallet": "Carteira",
    "topbar.chat": "Chat",
    "topbar.signIn": "Entrar",
    "topbar.register": "Registar",
    "common.email": "Endereço de e-mail",
    "topbar.signOut": "Sair",
    "lobby.searchAll": "Pesquisar todos os jogos",
    "chat.signInToChat": "Entre para conversar",
  },
};
